import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import { getReplies } from 'api/Comment';

import { cn, iaRa } from 'lib/helpers/utility';

import Button from 'components/shared/Button/Button';
import Typography from 'components/shared/Typography/Typography';

import { useReplies } from '../useReplies';

import Comment from './Comment';
import CommentInput from './CommentInput';

const Replies = ({
  commentId,
  repliesCount: repliesCountProp,
  category,
  refId,
  creatorId,
  isExpanded,
  showReplyInput,
  onToggleExpanded,
  onToggleReplyInput
}) => {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery(
    ['comment-replies', commentId],
    () => getReplies(navigate, { commentId }),
    { enabled: isExpanded, refetchOnMount: false, refetchOnWindowFocus: false, staleTime: 30000 }
  );

  const { onReplyPostHandler, isCreatingReply, onReplyDeleteHandler, onReplySaveHandler } =
    useReplies(commentId, category, refId);

  const replies = iaRa(data?.replies);
  const repliesCount = replies.length || repliesCountProp;

  const handleReplyPost = async (replyValue) => {
    await onReplyPostHandler(replyValue);
    onToggleReplyInput(false);
  };

  // Don't render anything if not expanded and no replies and no reply input
  if (!isExpanded && repliesCount === 0 && !showReplyInput) {
    return null;
  }

  return (
    <>
      {repliesCount > 0 && (
        <Button
          size="small"
          variant="link"
          color="neutral"
          text={`${repliesCount} ${repliesCount === 1 ? 'reply' : 'replies'}`}
          icon={isExpanded ? 'chevron-up-linear' : 'chevron-down-linear'}
          onClick={onToggleExpanded}
          className="text-xs"
        />
      )}

      {isExpanded && (
        <div
          className={cn(
            'ml-2 flex w-full flex-col gap-3 pl-3',
            !isLoading &&
              repliesCount > 0 &&
              'border-b-0 border-l-[1px] border-r-0 border-t-0 border-solid border-neutral-300'
          )}>
          {isLoading ? (
            <Typography color="neutral-500" size="small">
              Loading replies...
            </Typography>
          ) : repliesCount > 0 ? (
            replies.map((reply) => (
              <Comment
                isReply
                key={reply.id}
                comment={reply}
                category={category}
                refId={refId}
                onDelete={onReplyDeleteHandler}
                onSave={onReplySaveHandler}
                creatorId={creatorId}
              />
            ))
          ) : (
            <Typography color="neutral-500" size="small">
              No replies yet
            </Typography>
          )}
        </div>
      )}

      {showReplyInput && (
        <div className="mt-2 w-full">
          <CommentInput
            focusOnMount
            onPost={handleReplyPost}
            loading={isCreatingReply}
            placeholder="Reply"
          />
        </div>
      )}
    </>
  );
};

export default Replies;
